import React, { useEffect, useState } from "react";

import { useField } from "formik";

import {
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { FileCopyOutlined } from "@material-ui/icons";

import { copyText } from "@ip-utils";

import { Chip } from "../../../index";
import Field from "../index";
import Currency from "./Currency";
import Entity from "./Entity";
import InvoiceLineCategory, {
  INVOICE_LINE_CATEGORIES,
} from "./InvoiceLineCategory";
import InvoiceType from "./InvoiceType";
import Status from "./Status";
import Tax from "./Tax";

export default function Select({
  copyable,
  inclusive,
  items,
  multiple,
  name,
  required,
  sort = true,
  ...props
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [, meta, helpers] = useField(name);

  useEffect(() => {
    return () => {
      if (copied) {
        window.clearTimeout(copied);
      }
    };
  });

  useEffect(() => {
    let options = [];
    if (Array.isArray(items)) {
      options = items.map((item) => ({
        key: item,
        value: item,
      }));
    } else if (items) {
      options = Object.entries(items).map(([key, value]) => ({
        key,
        value,
      }));
    }

    const keys = {};
    options = options.filter(({ key, value }) => {
      if (!value || keys[key]) {
        return false;
      }
      return (keys[key] = true);
    });

    if (sort) {
      options.sort((a, b) => {
        if (isNaN(a.value) && isNaN(b.value)) {
          return a.value < b.value ? -1 : 1;
        }
        return a.value.localeCompare(b.value);
      });
    }

    if (!required) {
      options.unshift({
        key: "",
        value: inclusive ? "All" : "None",
      });
    }

    setOptions(options);
  }, [inclusive, items, meta.value, name, required, sort]);

  function handleCopy() {
    const option = options.find((option) => option.key === meta.value);
    if (option) {
      copyText(option ? option.value : meta.value, () => {
        const timeout = window.setTimeout(setCopied, 2000, false);
        setCopied(timeout);
      });
    }
  }

  const InputProps = copyable && {
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title={copied ? "✓ Copied" : "Copy"}>
          <IconButton color="secondary" onClick={handleCopy}>
            <FileCopyOutlined fontSize="small" size="small" />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  };

  const SelectProps = {
    onClose: () => setOpen(false),
    onOpen: () => setOpen(true),
  };

  const extraProps = {};

  if (multiple) {
    SelectProps.multiple = true;
    SelectProps.renderValue = (selected) => (
      <Grid container spacing={1}>
        {selected.map((key) => {
          const option = options.find((option) => option.key === key);
          return (
            <Grid item key={key}>
              <Chip value={option ? option.value : key} />
            </Grid>
          );
        })}
      </Grid>
    );

    if (!required) {
      extraProps.onChange = (e) => {
        const { value } = e.target;
        if (value.includes("")) {
          value.splice(0, value.length);
        }
        helpers.setValue(value);
      };
    }
  }

  const temp = options.length <= (required ? 0 : 1) && meta.value;

  return (
    <Field
      InputProps={InputProps}
      name={name}
      required={required}
      select
      SelectProps={SelectProps}
      tooltipDisabled={open}
      {...extraProps}
      {...props}
    >
      {options.map(({ key, value }) => (
        <MenuItem key={key} value={key}>
          {value}
        </MenuItem>
      ))}
      {temp && <MenuItem value={temp} />}
    </Field>
  );
}

Select.Currency = Currency;
Select.Entity = Entity;
Select.InvoiceType = InvoiceType;
Select.Status = Status;
Select.Tax = Tax;
Select.InvoiceLineCategory = InvoiceLineCategory;
Select.InvoiceLineCategory.Items = INVOICE_LINE_CATEGORIES;
